import { assert } from '@ember/debug';
import { isEmpty, isNone, isPresent, typeOf } from '@ember/utils';

import window from 'ember-window-mock';

const MAX_COOKIE_BYTE_LENGTH = 4096;

/**
  Read the cookie value.
  @method readCookie
  @param {String} name The key to read from
  @return {String} A string which is the cookie value
  @public
*/
function readCookie(name) {
  let all = _getCookies();

  if (name) {
    return _decodeValue(all[name]);
  } else {
    Object.keys(all).forEach(name => (all[name] = _decodeValue(all[name])));
    return all;
  }
}

/**
  Write `value` in the browser cookie
  @method writeCookie
  @param {String} name The key to write to
  @param {String} value The value to write
  @param {Object} options Available options are: `domain`, `expires`, `maxAge`,
  `secure`, `path`
  @public
*/
function writeCookie(name, val, options = {}) {
  let value = _encodeValue(val);

  assert(
    'Cookies cannot be set with both maxAge and an explicit expiration time!',
    isEmpty(options.expires) || isEmpty(options.maxAge)
  );
  assert(
    `Cookies larger than ${MAX_COOKIE_BYTE_LENGTH} bytes are not supported by most browsers!`,
    _isCookieSizeAcceptable(value)
  );

  let serializedCookie = _serializeCookie(name, value, options);
  window.document.cookie = serializedCookie;
}

/**
  Clear the cookie.
  @method clearCookie
  @param {String} name The key to clear
  @public
*/
function clearCookie(name) {
  let options = { expires: new Date('1970-01-01') };
  writeCookie(name, null, options);
}

function _encodeValue(value) {
  if (isNone(value)) {
    return '';
  } else {
    return encodeURIComponent(value);
  }
}

function _decodeValue(value) {
  if (isNone(value)) {
    return value;
  } else {
    return decodeURIComponent(value);
  }
}

function _getCookies() {
  let all = window.document.cookie.split(';');
  let filtered = all.map(c => c.split('=')).filter(c => c.length === 2 && isPresent(c[0]));

  return filtered.reduce((acc, cookie) => {
    if (!isEmpty(cookie)) {
      let [key, value] = cookie;
      acc[key.trim()] = (value || '').trim();
    }
    return acc;
  }, Object.create(null));
}

function _isCookieSizeAcceptable(value) {
  // Counting bytes varies Pre-ES6 and in ES6
  // This snippet counts the bytes in the value
  // about to be stored as the cookie:
  // See https://stackoverflow.com/a/25994411/6657064
  let _byteCount = 0;
  let i = 0;
  let c;
  while ((c = value.charCodeAt(i++))) {
    _byteCount += c >> 11 ? 3 : c >> 7 ? 2 : 1;
  }

  return _byteCount < MAX_COOKIE_BYTE_LENGTH;
}

function _serializeCookie(name, value, options = {}) {
  let cookie = `${name}=${value}`;

  if (!isEmpty(options.domain)) {
    cookie = `${cookie}; domain=${options.domain}`;
  }
  if (typeOf(options.expires) === 'date') {
    cookie = `${cookie}; expires=${options.expires.toUTCString()}`;
  }
  if (!isEmpty(options.maxAge)) {
    cookie = `${cookie}; max-age=${options.maxAge}`;
  }
  if (options.secure) {
    cookie = `${cookie}; secure`;
  }
  if (!isEmpty(options.path)) {
    cookie = `${cookie}; path=${options.path}`;
  }

  return cookie;
}

export { _serializeCookie, clearCookie, readCookie, writeCookie };
